import { createErrorDetails, type XmaxErrorDetails } from './error-details'

export type XmaxErrorCode =
  | 'INVALID_API_KEY'
  | 'INVALID_MODEL'
  | 'INVALID_INPUT'
  | 'UNSUPPORTED_MEDIA'
  | 'MEDIA_PROCESSING_ERROR'
  | 'CAMERA_ERROR'
  | 'NETWORK_ERROR'
  | 'API_ERROR'
  | 'WEB_RTC_ERROR'
  | 'SESSION_ERROR'
  | 'OFFLINE_TASK_TIMEOUT'
  | 'UNKNOWN_ERROR'

/** Internal, privacy-safe request details that may be copied into probe events. */
export type XmaxErrorDiagnostics = {
  failure_type?: 'business_error' | 'http_error' | 'timeout' | 'network_error' | 'invalid_response' | 'cancelled'
  business_code?: string
  http_status?: number
}

const errorDiagnostics = new WeakMap<object, XmaxErrorDiagnostics>()

/** @internal Attach only enumerated/coded diagnostics; never attach response messages or bodies. */
export function attachErrorDiagnostics<T>(error: T, diagnostics: XmaxErrorDiagnostics): T {
  if (error && (typeof error === 'object' || typeof error === 'function')) {
    const previous = errorDiagnostics.get(error as object)
    errorDiagnostics.set(error as object, { ...previous, ...diagnostics })
  }
  return error
}

/** @internal Read privacy-safe diagnostics retained while an error crosses SDK layers. */
export function getErrorDiagnostics(error: unknown): XmaxErrorDiagnostics | undefined {
  if (!error || (typeof error !== 'object' && typeof error !== 'function')) {
    return undefined
  }
  return errorDiagnostics.get(error as object)
}

/** Public SDK error contract: stable code/message plus optional immutable diagnostic details. */
export class XmaxSdkError extends Error {
  readonly code: XmaxErrorCode
  declare readonly details?: XmaxErrorDetails

  constructor(code: XmaxErrorCode, message: string) {
    super(message)
    Object.defineProperty(this, 'name', {
      value: 'XmaxSdkError',
      configurable: true,
      writable: true,
      enumerable: false,
    })
    Object.defineProperty(this, 'message', {
      value: message,
      configurable: true,
      writable: true,
      enumerable: true,
    })
    this.code = code
  }
}

/** @internal Enrich the same error without changing its code or callback identity. */
export function attachPublicErrorDetails(error: XmaxSdkError, values: Record<string, unknown>): XmaxSdkError {
  const merged = { ...values, ...error.details }
  if (error.details?.reason_code) {
    // Absence is part of the original operation's identity; never borrow a later task.
    for (const key of ['trace_id', 'session_id', 'generation_id'] as const) {
      if (!(key in error.details)) delete merged[key]
    }
  }
  const details = createErrorDetails(merged)
  try {
    if (Object.keys(details).length) Object.defineProperty(error, 'details', {
      value: details, enumerable: true, configurable: true,
    })
  } catch { /* A consumer may freeze the error; diagnostics must not replace it. */ }
  return error
}

export function isXmaxSdkError(error: unknown): error is XmaxSdkError {
  return error instanceof XmaxSdkError
}

export type XmaxErrorFallback = {
  code: XmaxErrorCode
  message: string
}

/** Preserve a useful native message while assigning a stable SDK code. */
export function toXmaxSdkError(error: unknown, fallback: XmaxErrorFallback): XmaxSdkError {
  if (isXmaxSdkError(error)) {
    return error
  }
  const nativeMessage = error instanceof Error ? error.message.trim() : ''
  return attachErrorDiagnostics(new XmaxSdkError(fallback.code, nativeMessage || fallback.message), getErrorDiagnostics(error) ?? {})
}
